import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { CustomSelect } from '@/components/ui/customSelect';
import { z } from 'zod';
import { useGetAssetByIdQuery, useUpdateAssetMutation } from '@/store/api/assetsApi';
import { ErrorBanner } from '@/components/common/ErrorBanner';
import { useNavigate, useParams } from 'react-router-dom';
import { getMetaValue } from '@/utils/assetMeta';
import { BackButton } from '@/components/common/BackButton';

const editAssetSchema = z.object({
  titleStatus: z.string().min(1, 'Title Status is required'),
  builtupArea: z.string().refine((v) => parseFloat(v) > 0, 'Builtup area must be greater than 0'),
  landArea: z.string().refine((v) => parseFloat(v) > 0, 'Land area must be greater than 0'),
  yearOfConstruction: z.number().min(1800).max(new Date().getFullYear()),
  assetAge: z.number().min(0).max(500).optional(),
  marketValue: z.string().min(1, 'Market Value is required'),
  rentalIncomeMonthly: z.string().min(1, 'Rental Income Monthly is required'),

  leaseTenureRemainingMonths: z.number().min(0, 'Lease tenure cannot be negative'),

  last3YearRevenue: z.string().min(1, 'Last 3 Year Revenue is required'),
  initialPricePerFraction: z
    .number({
      required_error: 'Price per fraction is required',
      invalid_type_error: 'Price must be a number',
    })
    .gt(0, 'Price per fraction must be greater than 0'),
});

type EditAssetFormData = z.infer<typeof editAssetSchema>;

const EditAssetPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const {
    data: asset,
    isLoading,
    isError,
  } = useGetAssetByIdQuery(Number(id), {
    skip: !id,
  });

  const [updateAsset, { isLoading: updatingAsset, error }] = useUpdateAssetMutation();

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 150 }, (_, i) => currentYear - i);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<EditAssetFormData>({
    resolver: zodResolver(editAssetSchema),
  });

  const handleUpdateAsset = async (data: EditAssetFormData) => {
    console.log(data, 'data');
    try {
      await updateAsset({
        id: Number(id),
        ...data,
      }).unwrap();
    } catch (err) {
      console.error('Failed to update asset status', err);
    }
  };

  useEffect(() => {
    if (asset) {
      reset({
        titleStatus: getMetaValue(asset.meta, 'titleStatus'),
        builtupArea: getMetaValue(asset.meta, 'builtupArea'),
        landArea: getMetaValue(asset.meta, 'landArea'),
        yearOfConstruction: Number(getMetaValue(asset.meta, 'yearOfConstruction')),
        assetAge: Number(getMetaValue(asset.meta, 'assetAge')),
        marketValue: getMetaValue(asset.meta, 'marketValue'),
        rentalIncomeMonthly: getMetaValue(asset.meta, 'rentalIncomeMonthly'),
        leaseTenureRemainingMonths:
          Number(getMetaValue(asset.meta, 'leaseTenureRemainingMonths')) || 0,
        last3YearRevenue: getMetaValue(asset.meta, 'last3YearRevenue') || '0',
        initialPricePerFraction: Number(asset?.price?.initialPricePerFraction) || 0,
      });
    }
  }, [asset, reset]);

  return (
    <form onSubmit={handleSubmit(handleUpdateAsset)} className="space-y-10 max-w-4xl">
      <div className="flex items-center gap-4">
        <BackButton />
        <h2 className="text-xl font-bold">Edit Asset Details</h2>
      </div>

      {error && (
        <ErrorBanner
          type="error"
          title="Update Asset Failed"
          message={(error as any)?.data?.message || 'Something went wrong'}
        />
      )}
      {/* ================= EDITABLE FIELDS ================= */}
      <h2 className="text-xl font-bold">Property Details</h2>

      <div className="grid grid-cols-3 gap-6">
        {/* Title Status */}
        <div className="space-y-2">
          <Label required>Title Status</Label>
          <CustomSelect {...register('titleStatus')}>
            <option value="" disabled>
              Select Title Status
            </option>
            <option value="clear">Clear Title</option>
            <option value="disputed">Disputed</option>
            <option value="litigation">Under Litigation</option>
            <option value="others">Others</option>
          </CustomSelect>
          {errors.titleStatus && (
            <p className="text-sm text-destructive">{errors.titleStatus.message}</p>
          )}
        </div>

        {/* Builtup Area */}
        <div className="space-y-2">
          <Label required>Builtup Area (Sq.ft / Meter)</Label>
          <Input {...register('builtupArea')} />
          {errors.builtupArea && (
            <p className="text-sm text-destructive">{errors.builtupArea.message}</p>
          )}
        </div>

        {/* Land Area */}
        <div className="space-y-2">
          <Label required>Land Area (Sq.ft / Meter)</Label>
          <Input {...register('landArea')} />
          {errors.landArea && <p className="text-sm text-destructive">{errors.landArea.message}</p>}
        </div>

        {/* Year of Construction */}
        <div className="space-y-2">
          <Label required>Year of Construction</Label>
          <CustomSelect {...register('yearOfConstruction', { valueAsNumber: true })}>
            <option value="">Select year</option>
            {years.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </CustomSelect>
          {errors.yearOfConstruction && (
            <p className="text-sm text-destructive">{errors.yearOfConstruction.message}</p>
          )}
        </div>

        {/* Asset Age */}
        <div className="space-y-2 col-span-2">
          <Label>Asset Age (Optional)</Label>
          <Input type="number" {...register('assetAge', { valueAsNumber: true })} />
          {errors.assetAge && <p className="text-sm text-destructive">{errors.assetAge.message}</p>}
        </div>
      </div>

      <h2 className="text-xl font-bold">Financial & Valuation Details</h2>

      <div className="grid grid-cols-3 gap-6">
        {/* Builtup Area */}
        <div className="space-y-2">
          <Label required>Market Value</Label>
          <Input {...register('marketValue')} />
          {errors.marketValue && (
            <p className="text-sm text-destructive">{errors.marketValue.message}</p>
          )}
        </div>

        {/* Land Area */}
        <div className="space-y-2">
          <Label required>Rental Income (Monthly)</Label>
          <Input {...register('rentalIncomeMonthly')} />
          {errors.rentalIncomeMonthly && (
            <p className="text-sm text-destructive">{errors.rentalIncomeMonthly.message}</p>
          )}
        </div>
        <div className="space-y-2">
          <Label required>Lease Tenure Remaining (Months)</Label>
          <Input
            type="number"
            {...register('leaseTenureRemainingMonths', {
              valueAsNumber: true,
            })}
          />
          {errors.leaseTenureRemainingMonths && (
            <p className="text-sm text-destructive">{errors.leaseTenureRemainingMonths.message}</p>
          )}
        </div>
        <div className="space-y-2">
          <Label required>Last 3 Year Revenue</Label>
          <Input {...register('last3YearRevenue')} />
          {errors.last3YearRevenue && (
            <p className="text-sm text-destructive">{errors.last3YearRevenue.message}</p>
          )}
        </div>
        <div className="space-y-2">
          <Label required>Price Per Fraction</Label>
          <Input {...register('initialPricePerFraction')} />
          {errors?.initialPricePerFraction && (
            <p className="text-sm text-destructive">{errors?.initialPricePerFraction.message}</p>
          )}
        </div>
      </div>
      {/* ================= ACTIONS ================= */}
      <div className="flex justify-between">
        <Button type="button" variant="outline" onClick={() => navigate(-1)}>
          Cancel
        </Button>
        {/* <Button disabled={updatingAsset} type="submit">
          Save Changes
        </Button> */}
      </div>
    </form>
  );
};

export default EditAssetPage;
